/**
 * Calculadora de frete PDP — usa carriers Shopify (Frenet via /cart/shipping_rates.json).
 * Snapshot do carrinho → cotação do variant selecionado → restaura carrinho.
 */
import { CartUpdateEvent } from '@theme/events';

(function () {
  const SELECTOR = '[data-bp-shipping]';

  /**
   * @param {string} value
   * @returns {string}
   */
  function digitsOnly(value) {
    return String(value || '').replace(/\D/g, '');
  }

  /**
   * @param {string} digits
   * @returns {string}
   */
  function formatCep(digits) {
    const d = digits.slice(0, 8);
    if (d.length <= 5) return d;
    return `${d.slice(0, 5)}-${d.slice(5)}`;
  }

  /**
   * @param {string} price
   * @param {string} currency
   * @returns {string}
   */
  function formatMoney(price, currency) {
    const amount = Number(price);
    if (Number.isNaN(amount)) return price;
    try {
      return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: currency || 'BRL',
      }).format(amount);
    } catch {
      return `R$ ${amount.toFixed(2).replace('.', ',')}`;
    }
  }

  /**
   * @param {number[]|null|undefined} days
   * @returns {string}
   */
  function formatDeliveryDays(days) {
    if (!Array.isArray(days) || !days.length) return '';
    const min = Number(days[0]);
    const max = Number(days[days.length - 1]);
    if (Number.isNaN(min)) return '';
    if (min === max) {
      return min === 1 ? '1 dia útil' : `${min} dias úteis`;
    }
    return `${min} a ${max} dias úteis`;
  }

  /**
   * @param {HTMLElement} root
   * @returns {number|null}
   */
  function resolveVariantId(root) {
    const scope =
      root.closest('product-information, .product-information, .shopify-section') || document;
    const idInput = scope.querySelector(
      'product-form-component form [name="id"], form[data-type="add-to-cart-form"] [name="id"], [name="id"]'
    );
    if (idInput && idInput.value) {
      const id = Number(idInput.value);
      return Number.isFinite(id) ? id : null;
    }
    const fallback = Number(root.dataset.variantId);
    return Number.isFinite(fallback) ? fallback : null;
  }

  /**
   * @param {HTMLElement} root
   * @returns {number}
   */
  function resolveQuantity(root) {
    const scope =
      root.closest('product-information, .product-information, .shopify-section') || document;
    const qtyInput = scope.querySelector('quantity-selector-component input[name="quantity"], input[name="quantity"]');
    const qty = qtyInput ? Number(qtyInput.value) : 1;
    return Number.isFinite(qty) && qty > 0 ? qty : 1;
  }

  /**
   * @param {string} cep
   * @returns {Promise<{uf: string, city: string}>}
   */
  async function resolveAddress(cep) {
    const res = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
    if (!res.ok) throw new Error('viacep');
    const data = await res.json();
    if (data.erro || !data.uf) throw new Error('cep');
    return { uf: data.uf, city: data.localidade || '' };
  }

  /**
   * @returns {Promise<object>}
   */
  async function fetchCart() {
    const res = await fetch('/cart.js', { credentials: 'same-origin' });
    if (!res.ok) throw new Error('cart');
    return res.json();
  }

  /**
   * @param {object} cart
   */
  function notifyCartUpdate(cart) {
    document.dispatchEvent(
      new CartUpdateEvent(cart, 'bp-shipping-calculator', {
        source: 'bp-shipping-calculator',
        itemCount: cart.item_count,
      })
    );
  }

  /**
   * @param {Array<{id: number, quantity: number, properties?: object}>} items
   */
  async function replaceCart(items) {
    await fetch('/cart/clear.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });

    if (!items.length) {
      return fetchCart();
    }

    const res = await fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ items }),
    });

    if (!res.ok) throw new Error('add');
    return fetchCart();
  }

  /**
   * @param {string} zip
   * @param {string} province
   * @returns {Promise<{shipping_rates?: Array<object>}>}
   */
  async function fetchShippingRates(zip, province) {
    const params = new URLSearchParams({
      'shipping_address[zip]': zip,
      'shipping_address[country]': 'Brazil',
      'shipping_address[province]': province,
    });
    const res = await fetch(`/cart/shipping_rates.json?${params}`, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });
    if (!res.ok) throw new Error('rates');
    return res.json();
  }

  /**
   * @param {HTMLElement} root
   * @param {'idle'|'loading'|'error'|'empty'|'results'} state
   * @param {string} [message]
   */
  function setStatus(root, state, message) {
    const status = root.querySelector('[data-bp-shipping-status]');
    const results = root.querySelector('[data-bp-shipping-results]');
    if (!status || !results) return;

    root.dataset.state = state;
    status.hidden = state === 'idle' || state === 'results';
    results.hidden = state !== 'results';

    if (state === 'loading') {
      status.textContent = root.dataset.msgLoading || 'Calculando…';
      status.hidden = false;
    } else if (state === 'error' || state === 'empty') {
      status.textContent = message || root.dataset.msgError || 'Não foi possível calcular o frete.';
      status.hidden = false;
      results.innerHTML = '';
    }
  }

  /**
   * @param {HTMLElement} root
   * @param {Array<object>} rates
   */
  function renderRates(root, rates) {
    const results = root.querySelector('[data-bp-shipping-results]');
    if (!results) return;

    results.innerHTML = '';
    const list = document.createElement('ul');
    list.className = 'bp-shipping__list';
    list.setAttribute('role', 'list');

    rates.forEach((rate) => {
      const li = document.createElement('li');
      li.className = 'bp-shipping__item';

      const name = document.createElement('span');
      name.className = 'bp-shipping__name';
      name.textContent = rate.presentment_name || rate.name || 'Frete';

      const meta = document.createElement('span');
      meta.className = 'bp-shipping__meta';
      const days = formatDeliveryDays(rate.delivery_days);
      const price = formatMoney(rate.price, rate.currency);
      meta.textContent = days ? `${price} · ${days}` : price;

      li.append(name, meta);
      list.appendChild(li);
    });

    results.appendChild(list);
    setStatus(root, 'results');
  }

  /**
   * @param {HTMLElement} root
   */
  async function calculate(root) {
    if (root.dataset.busy === 'true') return;

    const input = root.querySelector('[data-bp-shipping-input]');
    const button = root.querySelector('[data-bp-shipping-submit]');
    if (!input) return;

    const cep = digitsOnly(input.value);
    if (cep.length !== 8) {
      setStatus(root, 'error', root.dataset.msgInvalidCep || 'Informe um CEP válido.');
      input.focus();
      return;
    }

    const variantId = resolveVariantId(root);
    if (!variantId) {
      setStatus(root, 'error', root.dataset.msgVariant || 'Selecione uma variação.');
      return;
    }

    const quantity = resolveQuantity(root);
    root.dataset.busy = 'true';
    if (button) button.disabled = true;
    setStatus(root, 'loading');

    let snapshot = [];
    let restored = false;

    try {
      const cart = await fetchCart();
      snapshot = (cart.items || []).map((item) => ({
        id: item.variant_id,
        quantity: item.quantity,
        properties: item.properties || {},
      }));

      await replaceCart([{ id: variantId, quantity }]);

      const address = await resolveAddress(cep);
      const data = await fetchShippingRates(cep, address.uf);
      const rates = Array.isArray(data.shipping_rates) ? data.shipping_rates : [];

      await replaceCart(snapshot);
      restored = true;
      notifyCartUpdate(await fetchCart());

      if (!rates.length) {
        setStatus(root, 'empty', root.dataset.msgEmpty || 'Nenhuma opção de frete para este CEP.');
        return;
      }

      renderRates(root, rates);
    } catch (error) {
      try {
        if (!restored) {
          await replaceCart(snapshot);
          notifyCartUpdate(await fetchCart());
        }
      } catch {
        /* ignore restore errors */
      }

      const key = error && error.message;
      let message = root.dataset.msgError || 'Não foi possível calcular o frete.';
      if (key === 'cep') message = root.dataset.msgInvalidCep || 'CEP não encontrado.';
      setStatus(root, 'error', message);
    } finally {
      root.dataset.busy = 'false';
      if (button) button.disabled = false;
    }
  }

  /**
   * @param {HTMLElement} root
   */
  function bind(root) {
    if (root.dataset.bound === 'true') return;
    root.dataset.bound = 'true';

    const input = root.querySelector('[data-bp-shipping-input]');
    const form = root.querySelector('[data-bp-shipping-form]');

    if (input) {
      input.addEventListener('input', () => {
        const formatted = formatCep(digitsOnly(input.value));
        input.value = formatted;
      });
    }

    if (form) {
      form.addEventListener('submit', (event) => {
        event.preventDefault();
        calculate(root);
      });
    }
  }

  function init(root = document) {
    root.querySelectorAll(SELECTOR).forEach(bind);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => {
    init(event.target instanceof HTMLElement ? event.target : document);
  });
})();
